/**
 * Error Tracking & System Health Service
 * 
 * Focused on operational monitoring:
 * - User-facing errors (parents/children experiencing issues)
 * - System health checks
 * - Performance degradation
 * 
 * NOT for admin action auditing (unnecessary for single-admin platform).
 * 
 * @example
 * import { errorTracker } from '@/lib/logging';
 * 
 * // In a catch block:
 * errorTracker.capture('api_failure', error, { action: 'loading storybook' });
 * 
 * // For page crashes (error boundary):
 * errorTracker.pageCrash(error, componentStack);
 */

import { supabase } from '../supabase/client';

// =============================================================================
// TYPES
// =============================================================================

export type ErrorType = 
  | 'page_crash'      // React error boundary triggered
  | 'api_failure'     // Supabase or external API failed
  | 'auth_issue'      // Login/session problems
  | 'media_error'     // Audio/video playback failed
  | 'load_failure'    // Content failed to load
  | 'network_error'   // Connectivity issues
  | 'validation'      // Form/input errors
  | 'data_integrity'  // Corrupted data (e.g., invalid turn_code)
  | 'unknown';        // Catch-all

export interface ErrorContext {
  action?: string;           // What user was trying to do
  component?: string;        // React component name
  childId?: string;          // If child-specific
  storybookId?: string;      // If storybook-specific
  metadata?: Record<string, unknown>;
}

// =============================================================================
// ENVIRONMENT HELPERS
// =============================================================================

/**
 * Check if we're in development mode
 * Works with Vite's import.meta.env
 */
function isDev(): boolean {
  try {
    // Vite exposes DEV on import.meta.env
    return (import.meta as ImportMeta & { env?: { DEV?: boolean } }).env?.DEV === true;
  } catch {
    return false;
  }
}

// =============================================================================
// CONNECTION DETECTION
// =============================================================================

interface NavigatorWithConnection extends Navigator {
  connection?: {
    effectiveType?: string;
  };
}

function getConnectionType(): string {
  const nav = navigator as NavigatorWithConnection;
  if (nav.connection) {
    return nav.connection.effectiveType || 'unknown';
  }
  return 'unknown';
}

function getScreenSize(): string {
  return `${window.innerWidth}x${window.innerHeight}`;
}

// =============================================================================
// RATE LIMITING (prevent error spam)
// =============================================================================

const errorCache = new Map<string, number>();
const RATE_LIMIT_MS = 60000; // 1 minute between identical errors
const MAX_ERRORS_PER_SESSION = 50;
let sessionErrorCount = 0;

function shouldLogError(errorKey: string): boolean {
  // Check session limit
  if (sessionErrorCount >= MAX_ERRORS_PER_SESSION) {
    return false;
  }

  // Check rate limit for this specific error
  const lastLogged = errorCache.get(errorKey);
  const now = Date.now();
  
  if (lastLogged && (now - lastLogged) < RATE_LIMIT_MS) {
    return false;
  }
  
  errorCache.set(errorKey, now);
  sessionErrorCount++;
  return true;
}

// =============================================================================
// CORE ERROR TRACKER
// =============================================================================

export const errorTracker = {
  /**
   * Capture any error with context
   */
  async capture(
    type: ErrorType,
    error: Error | string,
    context?: ErrorContext
  ): Promise<void> {
    const errorObj = error instanceof Error ? error : new Error(String(error));
    const errorKey = `${type}:${errorObj.message}`;
    
    // Rate limit check
    if (!shouldLogError(errorKey)) {
      return;
    }

    // Console output for dev
    if (isDev()) {
      console.error(`[${type.toUpperCase()}]`, errorObj.message, context || '');
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get profile if user exists
      let profileId: string | undefined;
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle();
        profileId = profile?.id;
      }

      // Insert error log
      await supabase.from('user_error_log').insert({
        user_id: user?.id,
        profile_id: profileId,
        child_id: context?.childId,
        error_type: type,
        error_message: errorObj.message.slice(0, 500),
        error_stack: errorObj.stack?.slice(0, 2000),
        error_code: errorObj.name,
        page_url: window.location.href,
        component: context?.component,
        action: context?.action,
        user_agent: navigator.userAgent.slice(0, 500),
        screen_size: getScreenSize(),
        connection_type: getConnectionType(),
      });
    } catch (err) {
      // Silent fail - don't throw on logging failure
      if (isDev()) {
        console.error('[ErrorTracker] Failed to persist:', err);
      }
    }
  },

  /**
   * Capture a page crash (from error boundary)
   */
  pageCrash(error: Error, componentStack?: string): void {
    this.capture('page_crash', error, {
      component: componentStack?.split('\n')[1]?.trim() || 'Unknown',
      metadata: { componentStack },
    });
  },

  /**
   * Capture API failure
   */
  apiFailure(error: Error | string, endpoint?: string, context?: ErrorContext): void {
    this.capture('api_failure', error, {
      ...context,
      action: context?.action || `API call to ${endpoint}`,
    });
  },

  /**
   * Capture auth issue
   */
  authIssue(error: Error | string, action?: string): void {
    this.capture('auth_issue', error, { action });
  },

  /**
   * Capture media playback error
   */
  mediaError(error: Error | string, mediaUrl?: string, context?: ErrorContext): void {
    this.capture('media_error', error, {
      ...context,
      action: context?.action || 'Playing media',
      metadata: { mediaUrl, ...context?.metadata },
    });
  },

  /**
   * Capture content load failure
   */
  loadFailure(error: Error | string, resource?: string, context?: ErrorContext): void {
    this.capture('load_failure', error, {
      ...context,
      action: context?.action || `Loading ${resource}`,
    });
  },

  /**
   * Capture network error
   */
  networkError(error: Error | string, context?: ErrorContext): void {
    this.capture('network_error', error, context);
  },

  /**
   * Capture data integrity error (corrupted database data)
   * These are CRITICAL - they indicate data corruption that needs immediate fix.
   */
  dataIntegrity(error: Error | string, resource: string, context?: ErrorContext): void {
    this.capture('data_integrity', error, {
      ...context,
      action: context?.action || `Data integrity issue in ${resource}`,
    });
  },
};

// =============================================================================
// GLOBAL ERROR HANDLERS
// =============================================================================

let globalHandlersSetup = false;

/**
 * Set up global error handlers (call once in app init)
 */
export function setupGlobalErrorHandlers(): void {
  if (globalHandlersSetup) return;
  globalHandlersSetup = true;

  // Unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    // Determine error type based on message
    let errorType: ErrorType = 'unknown';
    const msg = error.message.toLowerCase();
    
    if (msg.includes('fetch') || msg.includes('network') || msg.includes('cors')) {
      errorType = 'network_error';
    } else if (msg.includes('auth') || msg.includes('session') || msg.includes('token')) {
      errorType = 'auth_issue';
    } else if (msg.includes('supabase') || msg.includes('api')) {
      errorType = 'api_failure';
    }

    errorTracker.capture(errorType, error, {
      action: 'Unhandled promise rejection',
    });
  });

  // Global errors
  window.addEventListener('error', (event) => {
    if (event.error) {
      errorTracker.capture('unknown', event.error, {
        action: 'Uncaught error',
        metadata: {
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
        },
      });
    }
  });

  // Network status changes
  window.addEventListener('offline', () => {
    errorTracker.capture('network_error', 'Device went offline', {
      action: 'Network status change',
    });
  });
}

// =============================================================================
// ERROR BOUNDARY HELPER
// =============================================================================

/**
 * Log an error from React Error Boundary
 */
export function logErrorBoundary(error: Error, errorInfo: React.ErrorInfo): void {
  errorTracker.pageCrash(error, errorInfo.componentStack || undefined);
}

// =============================================================================
// SIMPLE LOGGER (for debug/info/warn/error logging)
// =============================================================================

/**
 * Simple logger utility for consistent debug/info/warn/error output.
 * In production, these could be forwarded to a logging service.
 */
export const logger = {
  debug(...args: unknown[]): void {
    if (isDev()) {
      console.debug(...args);
    }
  },
  
  info(...args: unknown[]): void {
    console.info(...args);
  },
  
  warn(...args: unknown[]): void {
    console.warn(...args);
  },
  
  error(...args: unknown[]): void {
    console.error(...args);
  },
};

export default errorTracker;
